import api from "../../../api/api";
import { responseError } from "../../../api/apiError";
import { ErrorMessages } from "../../../constants/messages";
import { groupUDFsByCategory, loadUdfs, type UDFCategory } from "../ReportServices";
import {
  getColumnGeneratedFilters,
  normalizeApiFilters,
  toColumnDefinition,
  type ApiColumn,
  type ApiFilter,
  type ColumnDefinition,
  type EntityTypeItem,
  type FilterDefinition,
} from "../TemplateServices";
import type { RoleItem } from "./TemplateTabViewTypes";

type ApiEntityName = {
  name: string;
  entityCode: string;
};

type ApiRole = {
  roleId?: string;
  roleName?: string;
  name?: string;
  id?: string;
};

export type TemplateColumnLoadResult = {
  columns: ColumnDefinition[];
  generatedFilters: FilterDefinition[];
};

export async function fetchTemplateEntityNames(authToken: string, identityProvider: string): Promise<EntityTypeItem[]> {
  const response = await api.fetch("entity-name/names", authToken, identityProvider);
  if (!response.ok) {
    throw await responseError(response, ErrorMessages.LoadEntityTypesFailed);
  }

  const data = await response.json();
  return (Array.isArray(data) ? data : []).map((item: ApiEntityName) => ({
    key: item.entityCode,
    displayName: item.name,
    description: undefined,
    dataEndpoint: item.entityCode.toLowerCase(),
  }));
}

export async function fetchTemplateRoles(authToken: string, identityProvider: string): Promise<RoleItem[]> {
  const response = await api.fetch("entity-name/roles", authToken, identityProvider);
  if (!response.ok) {
    throw await responseError(response, ErrorMessages.LoadRolesFailed);
  }

  const data = await response.json();
  return (Array.isArray(data) ? data : [])
    .map((item: ApiRole | string) => {
      if (typeof item === "string") {
        return { id: item, name: item };
      }

      const id = item.roleId || item.id || item.name || "";
      const name = item.roleName || item.name || item.roleId || item.id || "";
      return { id, name };
    })
    .filter((role) => role.id !== "");
}

export async function fetchTemplateFilterDefinitions(
  entityCode: string,
  authToken: string,
  identityProvider: string
): Promise<FilterDefinition[]> {
  const response = await api.fetch(`entity-name/filters/${entityCode}`, authToken, identityProvider);
  if (!response.ok) {
    throw await responseError(response, ErrorMessages.LoadFiltersFailed);
  }

  const data = await response.json();
  return normalizeApiFilters((Array.isArray(data) ? data : []) as ApiFilter[], entityCode);
}

export async function fetchTemplateColumnDefinitions(
  entityCode: string,
  authToken: string,
  identityProvider: string
): Promise<TemplateColumnLoadResult> {
  const response = await api.fetch(`entity-name/columns/${entityCode}`, authToken, identityProvider);
  if (!response.ok) {
    throw await responseError(response, ErrorMessages.LoadColumnsFailed);
  }

  const data = await response.json();
  const apiColumns: ApiColumn[] = Array.isArray(data) ? data : [];
  return {
    columns: apiColumns.map((item) => toColumnDefinition(item)),
    generatedFilters: getColumnGeneratedFilters(apiColumns),
  };
}

export async function fetchTemplateUdfCategories(
  entityCode: string,
  authToken: string,
  identityProvider: string
): Promise<UDFCategory[]> {
  const rowItems = await loadUdfs(`${entityCode}&udf=*`, authToken, identityProvider);
  return rowItems && rowItems.length > 0 ? groupUDFsByCategory(rowItems) : [];
}
